import { z } from "zod";

import { normalizePhone } from "./crm-config";
import { modelYearSchema } from "./model-year";
import { serviceTypeKeys } from "./service-config";

// Vehicle catalog and site content vocabulary shared by the API, the seed, the
// management panel and the public site. Pure module: keys, labels, zod shapes.

export const fuelKeys = ["electric", "hybrid", "gasoline"] as const;
export type FuelKey = (typeof fuelKeys)[number];

export const fuelLabels = {
  electric: "برقی",
  hybrid: "هیبریدی",
  gasoline: "بنزینی",
} satisfies Record<FuelKey, string>;

export const bodyKeys = ["suv", "crossover", "sedan", "hatchback"] as const;
export type BodyKey = (typeof bodyKeys)[number];

export const bodyLabels = {
  suv: "شاسی‌بلند",
  crossover: "کراس‌اور",
  sedan: "سدان",
  hatchback: "هاچبک",
} satisfies Record<BodyKey, string>;

export const specKeys = [
  "engine",
  "power",
  "torque",
  "gearbox",
  "drivetrain",
  "acceleration",
  "topSpeed",
  "consumption",
  "length",
  "wheelbase",
  "cargo",
  "seats",
  "airbags",
  "brakes",
  "assist",
] as const;
export type SpecKey = (typeof specKeys)[number];

export const specLabels = {
  engine: "پیشرانه",
  power: "حداکثر قدرت",
  torque: "حداکثر گشتاور",
  gearbox: "جعبه‌دنده",
  drivetrain: "سیستم انتقال قدرت",
  acceleration: "شتاب ۰ تا ۱۰۰",
  topSpeed: "حداکثر سرعت",
  consumption: "مصرف ترکیبی / برد",
  length: "طول",
  wheelbase: "فاصله محوری",
  cargo: "حجم صندوق",
  seats: "ظرفیت سرنشین",
  airbags: "کیسه هوا",
  brakes: "سیستم ترمز",
  assist: "سامانه‌های کمک‌راننده",
} satisfies Record<SpecKey, string>;

export const specGroups: { title: string; keys: readonly SpecKey[] }[] = [
  {
    title: "پیشرانه و عملکرد",
    keys: [
      "engine",
      "power",
      "torque",
      "gearbox",
      "drivetrain",
      "acceleration",
      "topSpeed",
      "consumption",
    ],
  },
  { title: "ابعاد و فضا", keys: ["length", "wheelbase", "cargo", "seats"] },
  { title: "ایمنی", keys: ["airbags", "brakes", "assist"] },
];

const specText = z.string().trim().min(1, "این مشخصه خالی است").max(120);

export const vehicleSpecsSchema = z.object({
  engine: specText,
  power: specText,
  torque: specText,
  gearbox: specText,
  drivetrain: specText,
  acceleration: specText,
  topSpeed: specText,
  consumption: specText,
  length: specText,
  wheelbase: specText,
  cargo: specText,
  seats: specText,
  airbags: specText,
  brakes: specText,
  assist: specText,
} satisfies Record<SpecKey, typeof specText>);
export type VehicleSpecs = z.infer<typeof vehicleSpecsSchema>;

export const vehicleColorSchema = z.object({
  name: z.string().trim().min(2).max(40),
  value: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
export type VehicleColor = z.infer<typeof vehicleColorSchema>;

export const featureGroupSchema = z.object({
  group: z.string().trim().min(2).max(60),
  items: z.array(z.string().trim().min(2).max(120)).min(1).max(12),
});
export type FeatureGroup = z.infer<typeof featureGroupSchema>;

/** One item of a model's periodic-maintenance plan: due every N km or M months, whichever first */
export const maintenanceItemSchema = z.object({
  id: z.string().trim().min(2).max(30),
  title: z.string().trim().min(2).max(80),
  intervalKm: z.number().int().min(1000).max(500_000),
  intervalMonths: z.number().int().min(1).max(120).nullable(),
});
export type MaintenanceItem = z.infer<typeof maintenanceItemSchema>;

/** A plate sold without a published price: the visitor asks for it (a consult request or a call) */
export const PRICE_INQUIRY = "inquiry" as const;

/**
 * How a model is priced with one plate: a number of toman, PRICE_INQUIRY (sold with it, price
 * on request) or null (not sold with it).
 */
export const platePriceSchema = z
  .union([z.number().int().min(1), z.literal(PRICE_INQUIRY)])
  .nullable();
export type PlatePrice = z.infer<typeof platePriceSchema>;

/** The three choices staff have for a plate, in the order the panel lists them */
export const priceModes = ["none", "price", "inquiry"] as const;
export type PriceMode = (typeof priceModes)[number];
export const priceModeLabels = {
  none: "عرضه نمی‌شود",
  price: "قیمت عددی (تومان)",
  inquiry: "استعلام قیمت",
} satisfies Record<PriceMode, string>;

export const priceModeOf = (price: PlatePrice): PriceMode =>
  price === null ? "none" : price === PRICE_INQUIRY ? "inquiry" : "price";

/** What stands where a price would, on a plate priced on request */
export const priceInquiryLabel = priceModeLabels.inquiry;

/** The toman of a plate price; null when there is no number (on request, or not sold) */
export const priceAmount = (price: PlatePrice) => (price === PRICE_INQUIRY ? null : price);

// ponytail: a plate priced on request is stored as 0 in the same bigint column (catalog_vehicle,
// catalog_price_change), so there is no migration; give it a mode column if SQL ever
// aggregates catalog prices. Every read of those columns goes through readPlatePrices.
const STORED_INQUIRY = 0;

/** A plate price as its database column holds it */
export const storedPrice = (price: PlatePrice) =>
  price === PRICE_INQUIRY ? STORED_INQUIRY : price;

/** A price column back into a plate price */
export const readStoredPrice = (stored: number | null): PlatePrice =>
  stored === STORED_INQUIRY ? PRICE_INQUIRY : stored;

/** Both plate prices of a catalog_vehicle row */
export const readPlatePrices = (row: { price: number | null; priceFreeZone: number | null }) => ({
  priceNational: readStoredPrice(row.price),
  priceFreeZone: readStoredPrice(row.priceFreeZone),
});

/** Everything staff edit on a catalog vehicle (id, branch name and timestamps come from the server) */
export const vehicleInputSchema = z.object({
  name: z.string().trim().min(2).max(60),
  /** the same model in Latin letters, shown under the name; empty when it has none */
  nameLatin: z.string().trim().max(60).default(""),
  brand: z.string().trim().min(2).max(30),
  /** Gregorian model year */
  year: modelYearSchema,
  trim: z.string().trim().min(2).max(60),
  /** with a national plate; null = not sold with one (the model must be sold with a plate) */
  priceNational: platePriceSchema,
  /** with a free-zone plate; null = not sold with one */
  priceFreeZone: platePriceSchema.default(null),
  fuel: z.enum(fuelKeys),
  body: z.enum(bodyKeys),
  transmission: z.string().trim().min(2).max(40),
  keySpec: z.string().trim().min(2).max(60),
  badge: z.string().trim().min(2).max(40),
  /** where the showroom car stands; null = no branch yet */
  branchId: z.string().nullable(),
  available: z.boolean(),
  image: z.url(),
  tagline: z.string().trim().min(5).max(120),
  intro: z.string().trim().min(20).max(1200),
  highlights: z.array(z.string().trim().min(3).max(120)).min(1).max(8),
  gallery: z.array(z.url()).max(8),
  video: z.object({ src: z.url().or(z.literal("")), title: z.string().max(80) }),
  specs: vehicleSpecsSchema,
  features: z.array(featureGroupSchema).max(6),
  colors: z.array(vehicleColorSchema).min(1).max(8),
  deliveryDays: z.number().int().min(1).max(365),
  warranty: z.string().trim().min(3).max(120),
  warrantyYears: z.number().int().min(0).max(15),
  warrantyKm: z.number().int().min(0).max(1_000_000),
  maintenance: z.array(maintenanceItemSchema).max(20),
  /** position in the public catalog and on the homepage (lower first) */
  sortOrder: z.number().int().min(0).max(999).default(0),
});
export type VehicleInput = z.infer<typeof vehicleInputSchema>;

export const plateKinds = ["national", "free_zone"] as const;
export type PlateKind = (typeof plateKinds)[number];
export const plateKindLabels = {
  national: "پلاک ملی",
  free_zone: "پلاک منطقه آزاد",
} satisfies Record<PlateKind, string>;

type PlatePrices = { priceNational: PlatePrice; priceFreeZone: PlatePrice };

/** a vehicle's price with this plate; null = not sold with it */
export const plateKindPrice = (prices: PlatePrices, kind: PlateKind) =>
  kind === "national" ? prices.priceNational : prices.priceFreeZone;

/**
 * The plate a model is listed with: the first (national first) that has a published price,
 * else the first it is sold with on request.
 */
export const primaryPlateKind = (prices: PlatePrices): PlateKind =>
  plateKinds.find((kind) => priceAmount(plateKindPrice(prices, kind)) !== null) ??
  plateKinds.find((kind) => plateKindPrice(prices, kind) !== null) ??
  "national";

/**
 * The price the catalog sorts, filters and computes installments by: that of primaryPlateKind.
 * PRICE_INQUIRY when no plate has a published price.
 */
export const primaryPrice = (prices: PlatePrices) =>
  plateKindPrice(prices, primaryPlateKind(prices)) ?? PRICE_INQUIRY;

/**
 * A plate price in a card or a table cell: «۲ میلیارد و ۸۵۰ میلیون تومان», «استعلام قیمت»,
 * or «—» where the model is not sold with the plate.
 */
export const platePriceWords = (price: PlatePrice) =>
  price === null ? "—" : price === PRICE_INQUIRY ? priceInquiryLabel : faTomanWords(price);

/**
 * The plate every compared model has a published price with (national first) and the ids
 * cheapest with it, so «کم‌هزینه‌ترین» never ranks a free-zone price against national ones;
 * null when fewer than two models or no plate they are all priced with.
 */
export function cheapestWithSamePlate(items: readonly ({ id: string } & PlatePrices)[]) {
  if (items.length < 2) return null;
  for (const kind of plateKinds) {
    const prices = items.flatMap((item) => priceAmount(plateKindPrice(item, kind)) ?? []);
    if (prices.length !== items.length) continue;
    const lowest = Math.min(...prices);
    return {
      kind,
      ids: items.filter((_, index) => prices[index] === lowest).map((item) => item.id),
    };
  }
  return null;
}

const faGrouped = new Intl.NumberFormat("fa-IR");
const moneyUnits = [
  [1_000_000_000, "میلیارد"],
  [1_000_000, "میلیون"],
  [1_000, "هزار"],
] as const;

/**
 * A price the way it is said: «۲ میلیارد و ۸۵۰ میلیون تومان». Every non-zero part is kept,
 * so it is as exact as the grouped digits (which can go in a title beside it).
 */
export function faTomanWords(value: number) {
  let rest = Math.round(value);
  const parts: string[] = [];
  for (const [size, name] of moneyUnits) {
    const count = Math.floor(rest / size);
    if (count > 0) parts.push(`${faGrouped.format(count)} ${name}`);
    rest -= count * size;
  }
  if (rest > 0 || parts.length === 0) parts.push(faGrouped.format(rest));
  return `${parts.join(" و ")} تومان`;
}

const faPercentFormat = new Intl.NumberFormat("fa-IR", {
  style: "percent",
  maximumFractionDigits: 2,
});

/** 25 → «۲۵٪», 2.5 → «۲٫۵٪»: the one percent wording of web and admin (sign after the number) */
export const faPercent = (percent: number) => faPercentFormat.format(percent / 100);

/** Persian refusal when a catalog vehicle is sold with neither plate */
export const missingPriceReason =
  "خودرو باید دست‌کم با یکی از دو پلاک (ملی یا منطقه آزاد) عرضه شود: قیمت بدهید یا «استعلام قیمت» را انتخاب کنید.";

/** Public URL of a catalog vehicle, e.g. atlas-x7; staff may pick it when creating a model */
export const vehicleSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "نشانی فقط حروف لاتین کوچک، رقم و خط تیره")
  .min(2)
  .max(40);

export const sortKeys = ["cheapest", "expensive"] as const;
export type SortKey = (typeof sortKeys)[number];

/** a price bound in the /vehicles URL, toman */
const priceBound = z.number().int().min(0).max(1_000_000_000_000);

/** The /vehicles filter URL contract: every part optional, unknown values ignored */
export const vehicleFilterFields = {
  fuel: z.enum(fuelKeys).optional(),
  body: z.enum(bodyKeys).optional(),
  /** only models sold with this plate, each priced, sorted and ranged by its price with it */
  plate: z.enum(plateKinds).optional(),
  brand: z.string().trim().min(1).max(30).optional(),
  /** price range, inclusive: of the chosen plate, else the primary price */
  min: priceBound.optional(),
  max: priceBound.optional(),
  sort: z.enum(sortKeys).optional(),
};
export const vehicleFilterSchema = z.object(vehicleFilterFields);
export type VehicleFilter = z.infer<typeof vehicleFilterSchema>;

export const postKinds = ["news", "article"] as const;
export type PostKind = (typeof postKinds)[number];

export const postKindLabels = {
  news: "خبر",
  article: "مقاله",
} satisfies Record<PostKind, string>;

export const postInputSchema = z.object({
  title: z.string().trim().min(5).max(140),
  kind: z.enum(postKinds),
  /** shown on the magazine card, e.g. «راهنمای خرید» */
  category: z.string().trim().max(40).default(""),
  summary: z.string().trim().max(300).default(""),
  body: z.string().trim().max(20_000).default(""),
  image: z.url().or(z.literal("")).default(""),
  readTime: z.string().trim().max(20).default(""),
});
export type PostInput = z.infer<typeof postInputSchema>;

export const bannerPositions = ["home_top", "home_middle", "vehicles_page"] as const;
export type BannerPosition = (typeof bannerPositions)[number];

export const bannerPositionLabels = {
  home_top: "بالای صفحه اصلی",
  home_middle: "میانه صفحه اصلی",
  vehicles_page: "صفحه خودروها",
} satisfies Record<BannerPosition, string>;

export const bannerInputSchema = z.object({
  title: z.string().trim().min(3).max(120),
  position: z.enum(bannerPositions),
  image: z.url().or(z.literal("")).default(""),
  href: z.string().trim().max(200).default(""),
  active: z.boolean(),
});
export type BannerInput = z.infer<typeof bannerInputSchema>;

/** Blocks of the public homepage, in page order; staff switch them on and off */
export const homepageSectionKeys = [
  "hero",
  "finder",
  "finance",
  "reviews",
  "guides",
  "branches",
] as const;
export type HomepageSectionKey = (typeof homepageSectionKeys)[number];

export const homepageSectionLabels = {
  hero: "هیرو و پیام برند",
  finder: "جستجوگر خودرو",
  finance: "ماشین‌حساب اقساط",
  reviews: "نظرات مشتریان",
  guides: "مجله خودرو",
  branches: "شعب و راه‌های تماس",
} satisfies Record<HomepageSectionKey, string>;

export const testimonialInputSchema = z.object({
  /** «خرید خودرو» / «خدمات خودرو» */
  kind: z.string().trim().min(2).max(40),
  quote: z.string().trim().min(10).max(400),
  name: z.string().trim().min(2).max(60),
  detail: z.string().trim().max(80).default(""),
  published: z.boolean(),
});
export type TestimonialInput = z.infer<typeof testimonialInputSchema>;

/** A time of day as minutes after midnight, Tehran */
const clock = z
  .number()
  .int()
  .min(0)
  .max(24 * 60);

// --- working days -----------------------------------------------------------------
// A branch is closed on its weekly closed days and on every official holiday. Days are
// Tehran calendar days, «YYYY-MM-DD»; the server's copy of the rule is branch-schedule.ts.

/** JS weekdays (0 = Sunday … 6 = Saturday) in the order of the Persian week, Saturday first */
export const persianWeek = [6, 0, 1, 2, 3, 4, 5] as const;

/** Name of a JS weekday (0 = Sunday) */
export const weekdayLabels = [
  "یکشنبه",
  "دوشنبه",
  "سه‌شنبه",
  "چهارشنبه",
  "پنجشنبه",
  "جمعه",
  "شنبه",
] as const;

/** Weekly closed days of a branch: distinct, in Persian week order (stored as jsonb) */
export const closedWeekdaysSchema = z
  .array(z.number().int().min(0).max(6))
  .max(7)
  .transform((days): number[] => persianWeek.filter((day) => days.includes(day)));

/** Weekday of a calendar day «YYYY-MM-DD»; a date has the same weekday in every zone */
export const weekdayOfDay = (day: string) => new Date(`${day}T00:00:00Z`).getUTCDay();

export type HolidayDto = { day: string; title: string };

/** An official holiday staff add: the Tehran calendar day and what it is */
export const holidayInputSchema = z.object({
  day: z.iso.date(),
  title: z.string().trim().min(2).max(80),
});

/**
 * Why the branch takes no service visit on this Tehran day, or null. `holidays` needs to
 * cover the day only; the list the caller has for a wider range is fine.
 */
export function closedDayReason(
  branch: { name: string; closedWeekdays: readonly number[] },
  day: string,
  holidays: readonly HolidayDto[],
) {
  const official = holidays.find((item) => item.day === day);
  if (official) return `این روز تعطیل رسمی است: ${official.title}.`;
  if (branch.closedWeekdays.includes(weekdayOfDay(day))) {
    return `شعبه ${branch.name} در این روز تعطیل است.`;
  }
  return null;
}

/** «جمعه‌ها» or «پنجشنبه‌ها و جمعه‌ها»; empty when the branch opens every day */
export const closedDaysLabel = (closedWeekdays: readonly number[]) => {
  const names = persianWeek
    .filter((day) => closedWeekdays.includes(day))
    .map((day) => `${weekdayLabels[day]}‌ها`);
  const last = names.pop();
  if (last === undefined) return "";
  return names.length === 0 ? last : `${names.join("، ")} و ${last}`;
};

/**
 * A branch's phone, stored as Latin digits only (the site dials it as is): a mobile (11 digits
 * from 09), a landline with its area code (0 + two digits + a 4-8 digit number, so 02192199
 * passes), a local or short number (4-8 digits), or empty.
 */
export const branchPhoneSchema = z
  .string()
  .trim()
  .max(20)
  .regex(/^[+\d۰-۹٠-٩\s-]*$/, "فقط رقم، فاصله و خط تیره")
  .transform(normalizePhone)
  .refine(
    (phone) => phone === "" || /^(?:09\d{9}|0[1-8]\d{5,9}|[1-9]\d{3,7})$/.test(phone),
    "تلفن را با پیش‌شماره وارد کنید؛ مثلاً ۰۷۶۴۴۴۲۱۲۳۴",
  );

export const branchInputSchema = z.object({
  /** true = a dealership under Taban Khodro; false (or left out on create) = official branch */
  dealership: z.boolean().optional(),
  /** true = a parts warehouse, never shown to customers (see BranchKind) */
  warehouse: z.boolean().optional(),
  name: z.string().trim().min(2).max(60),
  address: z.string().trim().min(5).max(200),
  // no zod defaults in this shape: `.partial()` of it is the update input, and a default
  // would turn a field the caller left out into an actual write that erases the stored value
  phone: branchPhoneSchema,
  salesOpen: clock,
  salesClose: clock,
  /** the service bay's window: the bookable slots of a day are generated inside it */
  serviceOpen: clock,
  serviceClose: clock,
  /** what may be booked here; empty is a branch that sells but takes no service visit */
  services: z.array(z.enum(serviceTypeKeys)),
  lat: z.number().min(-90).max(90).nullable(),
  lng: z.number().min(-180).max(180).nullable(),
  /** service appointments bookable per working day */
  dailyCapacity: z.number().int().min(0).max(100),
  active: z.boolean(),
  /** weekly closed days; left out keeps the stored ones (a new branch: Friday) */
  closedWeekdays: closedWeekdaysSchema.optional(),
});
export type BranchInput = z.infer<typeof branchInputSchema>;

/**
 * What a branch row is: an official Taban branch, a dealership under it (part of the
 * services), or a parts warehouse (holds and ships stock, never seen or booked by customers)
 */
export const branchKinds = ["official", "dealership", "warehouse"] as const;
export type BranchKind = (typeof branchKinds)[number];

export const branchKindLabels = {
  official: "شعبه رسمی تابان",
  dealership: "نمایندگی زیرمجموعه تابان خودرو",
  warehouse: "انبار قطعه",
} satisfies Record<BranchKind, string>;

export const branchKindOf = (row: { dealership: boolean; warehouse: boolean }): BranchKind =>
  row.warehouse ? "warehouse" : row.dealership ? "dealership" : "official";

/**
 * Rules a saved branch must keep, as the Persian refusal the panel shows. Kept out of
 * the zod shape so an update may carry only the fields that changed and still be checked
 * against the row it is merged into.
 */
export const branchRuleError = (values: BranchInput) => {
  if (values.warehouse && values.dealership) return "انبار نمی‌تواند نمایندگی هم باشد.";
  if (values.warehouse && values.services.length > 0) {
    return "انبار نوبت خدمات نمی‌پذیرد؛ خدمات را خالی بگذارید.";
  }
  if (values.salesClose <= values.salesOpen) return "ساعت پایان فروش باید بعد از ساعت شروع باشد.";
  if (values.serviceClose <= values.serviceOpen) {
    return "ساعت پایان خدمات باید بعد از ساعت شروع باشد.";
  }
  if ((values.lat === null) !== (values.lng === null)) {
    return "عرض و طول جغرافیایی باید با هم وارد شوند.";
  }
  if (values.closedWeekdays && values.closedWeekdays.length >= persianWeek.length) {
    return "شعبه باید دست‌کم یک روز در هفته باز باشد.";
  }
  return null;
};

/** Opens the pin in whatever map app the visitor has; empty when the branch has none */
export const branchMapUrl = (lat: number | null, lng: number | null) =>
  lat === null || lng === null
    ? ""
    : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

/**
 * Directions to the pin in the map apps visitors here use (نشان and بلد first, then Google);
 * empty when the branch has no pin
 */
export const branchMapLinks = (lat: number | null, lng: number | null) =>
  lat === null || lng === null
    ? []
    : [
        { label: "نشان", href: `https://neshan.org/maps/@${lat},${lng},16z,0p` },
        {
          label: "بلد",
          href: `https://balad.ir/location?latitude=${lat}&longitude=${lng}&zoom=16`,
        },
        { label: "نقشه گوگل", href: branchMapUrl(lat, lng) },
      ];

/** Public consultation request from the homepage form; becomes a website Lead */
export const consultTopicKeys = ["buy", "finance", "compare", "trade", "custom"] as const;
export type ConsultTopicKey = (typeof consultTopicKeys)[number];

export const consultTopicLabels = {
  buy: "انتخاب و خرید خودرو",
  finance: "شرایط اقساط و تأمین مالی",
  compare: "مقایسه چند مدل",
  trade: "معاوضه و ارزش خودروی فعلی",
  custom: "سفارش خودروی خارج از کاتالوگ",
} satisfies Record<ConsultTopicKey, string>;

/** A car the catalog does not carry, as the buyer asks for it (stored on the Lead) */
export const customSpecSchema = z.object({
  brand: z.string().trim().min(2).max(40),
  model: z.string().trim().min(1).max(60),
  trim: z.string().trim().max(60).default(""),
  /** Gregorian model year; null = any recent one */
  modelYear: modelYearSchema.nullable().default(null),
});
export type CustomSpec = z.infer<typeof customSpecSchema>;

/** «تویوتا لندکروزر GR-S مدل ۲۰۲۶»: the spec in one line (Lead note, order spec) */
export const customSpecText = (spec: Pick<CustomSpec, "brand" | "model" | "trim" | "modelYear">) =>
  [
    spec.brand,
    spec.model,
    spec.trim,
    spec.modelYear === null
      ? ""
      : `مدل ${spec.modelYear.toLocaleString("fa-IR", { useGrouping: false })}`,
  ]
    .filter(Boolean)
    .join(" ");

/** The car a visitor would hand in; staff add plate and appraisal later (ownership-config) */
export const tradeInCarSchema = z.object({
  vehicle: z.string().trim().min(2).max(80),
  /** Gregorian model year */
  modelYear: modelYearSchema.nullable().default(null),
  mileageKm: z.number().int().min(0).max(2_000_000).nullable().default(null),
  note: z.string().trim().max(300).default(""),
});
export type TradeInCar = z.infer<typeof tradeInCarSchema>;

export const consultTimeKeys = ["morning", "afternoon", "any"] as const;
export type ConsultTimeKey = (typeof consultTimeKeys)[number];

export const consultTimeLabels = {
  morning: "صبح (۹ تا ۱۳)",
  afternoon: "بعدازظهر (۱۳ تا ۱۸)",
  any: "هر زمان",
} satisfies Record<ConsultTimeKey, string>;

const TEHRAN_OFFSET_MS = 3.5 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
/** the chosen call-back window, Tehran hours [from, to) */
const consultWindows = { morning: [9, 13], afternoon: [13, 18] } as const;

/**
 * When the agent should call a website consult back: right away for «هر زمان» or inside
 * the chosen window, otherwise when that window next opens (later today or tomorrow).
 */
export function consultDueAt(time: ConsultTimeKey, now: Date) {
  if (time === "any") return now;
  const [from, to] = consultWindows[time];
  const shifted = now.getTime() + TEHRAN_OFFSET_MS;
  const dayStart = shifted - (shifted % (24 * HOUR_MS)) - TEHRAN_OFFSET_MS;
  const hour = (shifted % (24 * HOUR_MS)) / HOUR_MS;
  if (hour >= from && hour < to) return now;
  const opens = dayStart + from * HOUR_MS;
  return new Date(hour < from ? opens : opens + 24 * HOUR_MS);
}

/** How the buyer means to pay (a Lead field; the site's consult form asks it too) */
export const purchaseMethodKeys = ["cash", "installment", "leasing"] as const;
export type PurchaseMethodKey = (typeof purchaseMethodKeys)[number];
export const purchaseMethodLabels = {
  cash: "نقدی",
  installment: "اقساطی",
  leasing: "لیزینگ",
} satisfies Record<PurchaseMethodKey, string>;

/**
 * Budget choices of the site's consult form, stored on the Lead as a toman range.
 * ponytail: fixed bands in billion toman; move them to site content when prices outgrow them.
 */
export const consultBudgetKeys = ["to2", "2to3", "3to5", "over5"] as const;
export type ConsultBudgetKey = (typeof consultBudgetKeys)[number];
const BILLION = 1_000_000_000;
export const consultBudgets = {
  to2: { label: "تا ۲ میلیارد تومان", min: null, max: 2 * BILLION },
  "2to3": { label: "۲ تا ۳ میلیارد تومان", min: 2 * BILLION, max: 3 * BILLION },
  "3to5": { label: "۳ تا ۵ میلیارد تومان", min: 3 * BILLION, max: 5 * BILLION },
  over5: { label: "بیش از ۵ میلیارد تومان", min: 5 * BILLION, max: null },
} satisfies Record<ConsultBudgetKey, { label: string; min: number | null; max: number | null }>;

/** Iranian mobile number after normalizePhone */
export const MOBILE_PATTERN = /^09\d{9}$/;

/** clipped, never rejected: a long referrer must not fail the visitor's form */
const clipped = (max: number) =>
  z
    .string()
    .default("")
    .transform((value) => value.trim().slice(0, max));

/** Where a website visitor came from, captured on landing and sent with every site form */
export const attributionSchema = z.object({
  utmSource: clipped(100),
  utmMedium: clipped(100),
  utmCampaign: clipped(100),
  /** external page that linked to the site; empty for direct visits */
  referrer: clipped(300),
  /** first path of the visit, with its query */
  landingPage: clipped(300),
});
export type Attribution = z.infer<typeof attributionSchema>;

export const consultInputSchema = z.object({
  name: z.string().trim().min(2).max(60),
  phone: z.string().trim().min(5).max(20),
  topic: z.enum(consultTopicKeys),
  time: z.enum(consultTimeKeys),
  note: z.string().trim().max(500).default(""),
  /** set from a vehicle page: becomes the Lead's vehicle of interest */
  vehicleId: z.string().max(40).default(""),
  /** the plate the visitor priced the vehicle with on its page */
  plateKind: z.enum(plateKinds).optional(),
  city: z.string().trim().max(40).default(""),
  purchaseMethod: z.enum(purchaseMethodKeys).optional(),
  budget: z.enum(consultBudgetKeys).optional(),
  /** true from the trade-in form */
  tradeIn: z.boolean().optional(),
  attribution: attributionSchema.optional(),
  /** the wanted colour: one of the vehicle's colours, or free text for a special order */
  color: z.string().trim().max(40).optional(),
  /** topic «custom»: the car the catalog does not carry (required then) */
  custom: customSpecSchema.optional(),
  /** the visitor's current car, offered as a trade-in (staff appraise it on the Lead) */
  tradeInCar: tradeInCarSchema.optional(),
});
export type ConsultInput = z.infer<typeof consultInputSchema>;

/** The public calculator's numbers; the yearly rate is the site's own (site content «اقساط»), never the visitor's */
export const financeInputSchema = z.object({
  price: z.number().int().positive().max(1_000_000_000_000),
  downPayment: z.number().int().nonnegative().max(1_000_000_000_000),
  months: z.number().int().min(1).max(120),
});

/** Loan payment for the public finance calculator (annuity formula, whole toman) */
export function calculateMonthlyPayment(
  price: number,
  downPayment: number,
  months: number,
  annualRate: number,
) {
  const principal = Math.max(0, price - downPayment);
  const monthlyRate = annualRate / 1200;
  if (monthlyRate === 0) return Math.ceil(principal / months);
  return Math.ceil(
    (principal * monthlyRate * (1 + monthlyRate) ** months) / ((1 + monthlyRate) ** months - 1),
  );
}
